(function () {
  'use strict';

  const DESKTOP_MQ = '(min-width: 700px)';
  const discountCache = new WeakMap();
  const initialStateCache = new WeakMap();
  const previewState = new WeakMap();

  function getCartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function decodeCode(raw) {
    if (!raw) return '';
    try { return atob(raw); } catch (e) { return raw; }
  }

  function cartJson(path, options) {
    return fetch(
      getCartRoot() + path,
      Object.assign(
        {
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        },
        options || {}
      )
    ).then(function (response) {
      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    });
  }

  function findCartLine(cart, variantId) {
    return (cart.items || []).find(function (item) {
      return item.variant_id === variantId;
    });
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents);
    if (Number.isNaN(cents)) return '';

    const settings = window.themeVariables?.settings || {};
    const formatString =
      format ||
      (settings.currencyCodeEnabled
        ? settings.moneyWithCurrencyFormat
        : settings.moneyFormat) ||
      '{{amount}}';

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision ?? 2);
      const parts = number.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands ?? ','));
      const fraction = parts[1] ? (decimal ?? '.') + parts[1] : '';
      return whole + fraction;
    }

    const match = formatString.match(placeholderRegex);
    const token = match ? match[1] : 'amount';
    let value = '';

    switch (token) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 0, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function fromLabel(template, price) {
    return template ? template.replace('@@', price) : price;
  }

  function getBlockControls(block) {
    return {
      checkbox: block.querySelector('.pcp-reveal-checkbox'),
      button: block.querySelector('.pcp-reveal-btn'),
    };
  }

  function setControlsDisabled(block, disabled) {
    const { checkbox, button } = getBlockControls(block);
    if (checkbox) checkbox.style.cursor = disabled ? 'wait' : '';
    if (button) button.style.cursor = disabled ? 'wait' : '';
  }

  function setControlState(block, revealed) {
    const { checkbox, button } = getBlockControls(block);
    const bar = block.querySelector('.pcp-toggle-bar');

    if (checkbox) checkbox.checked = revealed;

    if (button) {
      button.setAttribute('aria-expanded', revealed ? 'true' : 'false');
      button.setAttribute('aria-checked', revealed ? 'true' : 'false');
      button.classList.toggle('pcp-reveal-btn--active', revealed);
    }

    if (bar) bar.classList.toggle('pcp-toggle-bar--active', revealed);
  }

  function isPriceListMode(block) {
    return (
      block.dataset.pcpMode === 'price-list' ||
      !!block.closest('price-list')
    );
  }

  function getPriceHost(block) {
    return (
      block.closest('price-list') ||
      block.closest('.cts-card__price') ||
      block.parentElement
    );
  }

  function getPriceNodes(block) {
    const host = getPriceHost(block);
    if (!host) return { sale: null, compare: null };

    if (isPriceListMode(block) || host.matches?.('price-list')) {
      return {
        sale: host.querySelector('sale-price'),
        compare: host.querySelector('compare-at-price'),
      };
    }

    return {
      sale: host.querySelector('.cts-price--current, .cts-price--regular'),
      compare: host.querySelector('.cts-price--original'),
    };
  }

  function cacheInitialState(block) {
    if (initialStateCache.has(block)) return;

    const { sale, compare } = getPriceNodes(block);
    initialStateCache.set(block, {
      saleClass: sale ? sale.className : '',
      saleHtml: sale ? sale.innerHTML : '',
      compareClass: compare ? compare.className : '',
      compareHtml: compare ? compare.innerHTML : '',
      compareHidden: compare ? compare.hasAttribute('hidden') : true,
      compareExisted: !!compare,
    });
  }

  function setLabeledPrice(el, label, formatted) {
    if (!el) return;
    el.innerHTML =
      (label ? '<span class="sr-only">' + label + '</span>' : '') + formatted;
  }

  function fetchCartDiscountPrice(variantId, code) {
    let addedPreview = false;

    return cartJson('cart.js')
      .then(function (cart) {
        if (findCartLine(cart, variantId)) return cart;
        addedPreview = true;
        return cartJson('cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        }).then(function () {
          return cartJson('cart.js');
        });
      })
      .then(function () {
        return cartJson('cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ discount: code }),
        });
      })
      .then(function () {
        return cartJson('cart.js');
      })
      .then(function (cart) {
        const line = findCartLine(cart, variantId);
        if (!line || line.final_line_price <= 0) {
          throw new Error('Invalid cart discount price');
        }

        return {
          saleCents: line.final_line_price,
          previewAdded: addedPreview,
          previewLineKey: addedPreview ? line.key : null,
        };
      });
  }

  function clearDiscount() {
    return cartJson('cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ discount: '' }),
    }).catch(function () {});
  }

  function removePreviewLine(block) {
    const state = previewState.get(block);
    previewState.delete(block);
    if (!state || !state.previewAdded || !state.previewLineKey) {
      return Promise.resolve();
    }

    const lineKey = state.previewLineKey;
    return cartJson('cart.js')
      .then(function (cart) {
        const line = (cart.items || []).find(function (item) {
          return item.key === lineKey;
        });
        if (!line || line.quantity > 1) return;
        return cartJson('cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: lineKey, quantity: 0 }),
        });
      })
      .catch(function () {});
  }

  function anyOtherRevealed(exceptBlock) {
    return Array.from(document.querySelectorAll('.pcp-block--revealed[data-pcp-id]')).some(
      function (block) {
        return block !== exceptBlock;
      }
    );
  }

  function createPriceLoader() {
    const loader = document.createElement('span');
    loader.className = 'pcp-toggle-bar__loader';
    loader.setAttribute('aria-hidden', 'true');
    loader.innerHTML =
      '<span class="pcp-toggle-bar__loader-dot"></span>' +
      '<span class="pcp-toggle-bar__loader-dot"></span>' +
      '<span class="pcp-toggle-bar__loader-dot"></span>';
    return loader;
  }

  function findSalePriceLoader(sale) {
    if (!sale) return null;
    return sale.querySelector(':scope > .pcp-toggle-bar__loader');
  }

  function setSalePriceLoading(block, loading) {
    const { sale } = getPriceNodes(block);
    const host = getPriceHost(block);
    if (host) host.classList.toggle('pcp-price-host--loading', loading);

    if (!sale) return;

    // Clean up any legacy sibling loaders from previous versions.
    const sibling = sale.nextElementSibling;
    if (sibling && sibling.classList.contains('pcp-toggle-bar__loader')) {
      sibling.remove();
    }

    const existing = findSalePriceLoader(sale);
    if (loading) {
      sale.classList.add('text-on-sale', 'pcp-sale-loading');
      if (!existing) {
        sale.appendChild(createPriceLoader());
      }
    } else {
      sale.classList.remove('pcp-sale-loading');
      if (existing) existing.remove();
    }
  }

  function showRevealed(block, control, discountData) {
    cacheInitialState(block);

    const { sale } = getPriceNodes(block);
    if (!sale) return;

    const { saleCents } = discountData;
    const varies = control?.dataset.pcpVaries === '1';
    const fromTemplate = control?.dataset.pcpF;
    const saleFormatted = varies
      ? fromLabel(fromTemplate, formatMoney(saleCents))
      : formatMoney(saleCents);
    const saleLabel = control?.dataset.pcpSaleLabel || 'Sale price';
    const priceListMode = isPriceListMode(block) || !!block.closest('price-list');

    // Keep existing classes; only swap sale €. Leave compare-at-price untouched.
    if (priceListMode) {
      setLabeledPrice(sale, saleLabel, saleFormatted);
    } else {
      sale.textContent = saleFormatted;
    }

    block.classList.add('pcp-block--revealed');
    block.classList.remove('pcp-block--error');
    const host = getPriceHost(block);
    if (host) host.classList.add('pcp-price-host--revealed');
    setControlState(block, true);
  }

  function hideRevealed(block) {
    const cached = initialStateCache.get(block);
    const { sale, compare } = getPriceNodes(block);

    if (cached && sale) {
      sale.className = cached.saleClass;
      sale.innerHTML = cached.saleHtml;
    }

    if (compare) {
      if (compare.getAttribute('data-pcp-created') === '1' || (cached && !cached.compareExisted)) {
        compare.remove();
      } else if (cached) {
        compare.className = cached.compareClass;
        compare.innerHTML = cached.compareHtml;
        if (cached.compareHidden) {
          compare.hidden = true;
          compare.setAttribute('hidden', '');
        } else {
          compare.hidden = false;
          compare.removeAttribute('hidden');
        }
      }
    }

    block.classList.remove('pcp-block--revealed', 'pcp-block--loading', 'pcp-block--error');
    setSalePriceLoading(block, false);
    const host = getPriceHost(block);
    if (host) host.classList.remove('pcp-price-host--revealed');
    setControlState(block, false);
    discountCache.delete(block);

    const keepDiscount = anyOtherRevealed(block);
    removePreviewLine(block).then(function () {
      if (!keepDiscount) return clearDiscount();
    });
  }

  async function revealBlock(block, control) {
    if (block.classList.contains('pcp-block--loading')) return;

    const code = decodeCode((control?.dataset.pcpCode || '').trim());
    const variantId = parseInt(control?.dataset.pcpVariantId, 10) || 0;
    if (!code || !variantId) {
      block.classList.add('pcp-block--error');
      setControlState(block, false);
      return;
    }

    cacheInitialState(block);
    block.classList.add('pcp-block--loading');
    setControlsDisabled(block, true);
    setSalePriceLoading(block, true);

    let discountData = discountCache.get(block);

    try {
      if (!discountData) {
        const cartData = await fetchCartDiscountPrice(variantId, code);
        previewState.set(block, {
          previewAdded: cartData.previewAdded,
          previewLineKey: cartData.previewLineKey,
        });
        discountData = {
          saleCents: cartData.saleCents,
          varies: control.dataset.pcpVaries === '1',
        };
        discountCache.set(block, discountData);
      } else {
        // Re-apply code so cart discount stays active when toggling multiple cards.
        await cartJson('cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ discount: code }),
        }).catch(function () {});
      }
    } catch (error) {
      block.classList.add('pcp-block--error');
      block.classList.remove('pcp-block--loading');
      setSalePriceLoading(block, false);
      setControlsDisabled(block, false);
      setControlState(block, false);
      return;
    }

    setSalePriceLoading(block, false);
    showRevealed(block, control, discountData);
    block.classList.remove('pcp-block--loading');
    setControlsDisabled(block, false);
  }

  async function toggleBlock(block, button) {
    if (block.classList.contains('pcp-block--loading')) return;

    if (block.classList.contains('pcp-block--revealed')) {
      hideRevealed(block);
      return;
    }

    await revealBlock(block, button);
  }

  function onClick(event) {
    const button = event.target.closest('.pcp-reveal-btn');
    if (!button || button.disabled) return;

    const block = button.closest('.pcp-block');
    if (!block) return;

    event.preventDefault();
    toggleBlock(block, button);
  }

  function onChange(event) {
    const checkbox = event.target;
    if (!checkbox.matches('.pcp-reveal-checkbox') || checkbox.disabled) return;

    const block = checkbox.closest('.pcp-block');
    if (!block) return;

    if (checkbox.checked) {
      if (!block.classList.contains('pcp-block--revealed')) {
        revealBlock(block, checkbox);
      }
    } else if (block.classList.contains('pcp-block--revealed')) {
      hideRevealed(block);
    }
  }

  function syncControlsOnResize() {
    document.querySelectorAll('.pcp-block--revealed[data-pcp-id]').forEach((block) => {
      setControlState(block, true);
    });
  }

  function initBlocks(root) {
    (root || document).querySelectorAll('.pcp-block[data-pcp-id]').forEach((block) => {
      cacheInitialState(block);
      setControlState(block, false);
    });
  }

  function init() {
    initBlocks();
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);

    if (window.matchMedia) {
      const mq = window.matchMedia(DESKTOP_MQ);
      if (mq.addEventListener) {
        mq.addEventListener('change', syncControlsOnResize);
      } else if (mq.addListener) {
        mq.addListener(syncControlsOnResize);
      }
    }

    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.('.pcp-block[data-pcp-id]')) {
            initBlocks(node.parentElement || node);
          } else if (node.querySelector?.('.pcp-block[data-pcp-id]')) {
            initBlocks(node);
          }
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
